'use client';

import React, { useState } from 'react';
import { Info, ChevronDown, ChevronUp, CheckCircle, Circle, ExternalLink } from 'lucide-react';
import { PDStats, PDCycle, PD_REQUIREMENTS, DIDACTIC_CATEGORIES, EXPERIENTIAL_CATEGORIES, CATEGORY_INFO } from './types';
import { formatDateRange } from './utils';

interface PDEducationBannerProps {
  stats: PDStats | null;
  cycle: PDCycle | null;
}

export function PDEducationBanner({ stats, cycle }: PDEducationBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasDidactic = stats?.hasDidactic ?? false;
  const hasExperiential = stats?.hasExperiential ?? false;
  const allRequirementsMet = hasDidactic && hasExperiential && (stats?.totalHours ?? 0) >= PD_REQUIREMENTS.totalHours;

  return (
    <div className={`rounded-xl border ${allRequirementsMet ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
      {/* Main Banner */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${allRequirementsMet ? 'bg-green-100' : 'bg-blue-100'}`}>
            {allRequirementsMet ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <Info className="w-5 h-5 text-blue-600" />
            )}
          </div>
          <div className="flex-1">
            <h3 className={`font-semibold ${allRequirementsMet ? 'text-green-800' : 'text-blue-800'}`}>
              {allRequirementsMet ? 'Requirements Complete!' : 'CRPO Professional Development Requirements'}
            </h3>
            <p className={`text-sm mt-1 ${allRequirementsMet ? 'text-green-700' : 'text-blue-700'}`}>
              {allRequirementsMet ? (
                <>You have completed all PD requirements for this cycle.</>
              ) : (
                <>
                  You need <strong>{PD_REQUIREMENTS.totalHours} hours</strong> of professional development activities
                  {cycle && <> by {new Date(cycle.end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' , timeZone: 'America/Toronto'})}</>}.
                </>
              )}
            </p>

            {/* Requirements Checklist */}
            <div className="flex flex-wrap gap-4 mt-3">
              <RequirementCheck
                label="At least one didactic activity"
                met={hasDidactic}
              />
              <RequirementCheck
                label="At least one experiential activity"
                met={hasExperiential}
              />
              <RequirementCheck
                label={`${PD_REQUIREMENTS.totalHours} hours total`}
                met={(stats?.totalHours ?? 0) >= PD_REQUIREMENTS.totalHours}
              />
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`flex items-center gap-1 text-sm font-medium ${
              allRequirementsMet ? 'text-green-700 hover:text-green-800' : 'text-blue-700 hover:text-blue-800'
            }`}
          >
            {isExpanded ? (
              <>
                Less <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                Learn More <ChevronDown className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-blue-200 bg-white/50 p-4 space-y-4">
          {/* Cycle Info */}
          {cycle && (
            <div>
              <h4 className="font-medium text-gray-900 mb-1">Your Current Cycle</h4>
              <p className="text-sm text-gray-600">
                {formatDateRange(cycle.start, cycle.end)}
                {cycle.isFirstCycle && (
                  <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                    First Cycle
                  </span>
                )}
              </p>
              {stats && stats.daysRemaining > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  {stats.daysRemaining} days remaining
                </p>
              )}
            </div>
          )}

          {/* Activity Types */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full" />
                Didactic Activities
                <span className="text-xs text-gray-500 font-normal">(instruction-based)</span>
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                {DIDACTIC_CATEGORIES.map((cat) => (
                  <li key={cat} className="flex items-center gap-2">
                    <span className="text-gray-400">•</span>
                    {CATEGORY_INFO[cat].label}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full" />
                Experiential Activities
                <span className="text-xs text-gray-500 font-normal">(experience-based)</span>
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                {EXPERIENTIAL_CATEGORIES.map((cat) => (
                  <li key={cat} className="flex items-center gap-2">
                    <span className="text-gray-400">•</span>
                    {CATEGORY_INFO[cat].label}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Supervision Auto-Logging Info */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-medium text-purple-900 flex items-center gap-2 mb-2">
              <span className="w-2 h-2 bg-purple-500 rounded-full" />
              Supervision Hours Auto-Logged
            </h4>
            <p className="text-sm text-purple-700 mb-2">
              Your clinical supervision and peer meeting hours are automatically counted
              toward your PD requirements. These entries appear with a "Supervision" badge
              and cannot be edited in the PD tracker.
            </p>
            <ul className="text-xs text-purple-600 space-y-1">
              <li className="flex items-center gap-2">
                <span className="text-purple-400">•</span>
                Individual, Dyad, Group supervision → Clinical Supervision (Experiential)
              </li>
              <li className="flex items-center gap-2">
                <span className="text-purple-400">•</span>
                Peer meetings (with approved reflection) → Study Group (Experiential)
              </li>
            </ul>
          </div>

          {/* CRPO Link */}
          <div className="pt-2">
            <a
              href="https://crpo.ca/registrant-information/registrant-requirements/quality-assurance-program/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
            >
              View CRPO Requirements
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function RequirementCheck({ label, met }: { label: string; met: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      {met ? (
        <CheckCircle className="w-4 h-4 text-green-600" />
      ) : (
        <Circle className="w-4 h-4 text-gray-400" />
      )}
      <span className={`text-sm ${met ? 'text-green-700' : 'text-gray-600'}`}>
        {label}
      </span>
    </div>
  );
}
